import React, { useCallback, useMemo } from "react";
import moment from "moment";
import { generateDisplayName } from "../Task";

const formatDuration = (seconds) => {
  const sec = Number(seconds);
  if (!sec || isNaN(sec) || sec <= 0) return "";
  if (sec < 60) return `${sec} sek.`;
  const mins = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (mins < 60) {
    return remSec > 0 ? `${mins} min ${remSec} sek.` : `${mins} min.`;
  }
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) {
    return remMins > 0 ? `${hours} godz. ${remMins} min.` : `${hours} godz.`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days} dni ${remHours} godz.` : `${days} dni`;
};

const validateTask = (task) => {
  const errors = [];
  const now = moment().subtract(1, "minute");

  if (task.startAt) {
    const startDate = moment(task.startAt);
    if (startDate.isBefore(now)) {
      errors.push("Data rozpoczęcia nie może być w przeszłości.");
    }
  }

  if (task.endAt) {
    const endDate = moment(task.endAt);
    if (endDate.isBefore(now)) {
      errors.push("Data zakończenia nie może być w przeszłości.");
    }
    if (task.startAt && endDate.isBefore(moment(task.startAt))) {
      errors.push("Data zakończenia musi być późniejsza niż data rozpoczęcia.");
    }
  }

  if (task.everyNth !== undefined && task.everyNth !== null && task.everyNth !== "") {
    const val = Number(task.everyNth);
    if (isNaN(val) || val < 900) {
      errors.push("Odstęp wywołań cyklicznych musi wynosić co najmniej 900 sekund (15 minut).");
    }
  }

  return errors;
};

const TaskList = ({ 
  tasks, 
  onRemoveTask, 
  onUpdateDateTime, 
  onUpdateEveryNth, 
  onExecute,
  isExecuting 
}) => {
  const [customTaskIds, setCustomTaskIds] = React.useState(() => new Set());

  const handleRemoveTask = useCallback((taskId) => {
    onRemoveTask(taskId);
  }, [onRemoveTask]);

  const handleDateTimeChange = useCallback((taskId, field, value) => {
    onUpdateDateTime(taskId, field, value);
  }, [onUpdateDateTime]);

  const handleEveryNthChange = useCallback((taskId, value) => {
    onUpdateEveryNth(taskId, value);
  }, [onUpdateEveryNth]);

  const taskValidations = useMemo(() => {
    const map = new Map();
    tasks.forEach(task => {
      map.set(task.id, validateTask(task));
    });
    return map;
  }, [tasks]);

  const hasAnyErrors = useMemo(() => {
    return Array.from(taskValidations.values()).some(errs => errs.length > 0);
  }, [taskValidations]);

  if (tasks.length === 0) {
    return (
      <div className="card text-center my-3 border-dashed p-4 shadow-sm bg-light">
        <div className="card-body">
          <h5 className="text-muted font-weight-bold">Brak zleconych poleceń</h5>
          <p className="text-muted mb-0">
            Wybierz akcję z powyższego panelu sterowania, aby dodać polecenia do kolejki wykonania.
          </p>
        </div>
      </div>
    );
  }

  return (
    <section className="card my-3 shadow-sm border-0">
      <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
        <h5 className="m-0 font-weight-bold text-dark">
          Zlecenia oczekujące na wykonanie ({tasks.length})
        </h5>
        {hasAnyErrors ? (
          <span className="badge badge-danger p-2">
            Popraw błędy w zleceniach
          </span>
        ) : tasks.length >= 3 ? (
          <span className="badge badge-warning p-2">
            Limit 3 zleceń osiągnięty
          </span>
        ) : null}
      </div>

      <div className="card-body">
        <ul className="list-group mb-3">
          {tasks.map((task) => {
            const errors = taskValidations.get(task.id) || [];
            const hasError = errors.length > 0;

            return (
              <li 
                key={task.id} 
                className={`list-group-item mb-2 rounded border ${hasError ? 'border-danger bg-light-danger' : ''}`}
                style={hasError ? { backgroundColor: '#fff5f5' } : {}}
              >
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                  <div className="flex-grow-1">
                    <span className="font-weight-bold text-dark h6 mb-0">
                      {generateDisplayName(task)}
                      {!['DISABLE_SALE', 'ENABLE_SALE', 'LED', 'AGGREGATE', 'TEMPERATURE', 'PARAM_CHANGE'].includes(task.taskType) && 
                        task.value !== null && task.value !== undefined && ` (${task.value})`}
                    </span>
                    {task.parameterAddress !== undefined && task.parameterAddress !== null && (
                      <small className="text-muted d-block mt-1">
                        Adres parametru: <strong>{task.parameterAddress}</strong>
                      </small>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm ml-2"
                    aria-label={`Usuń polecenie: ${generateDisplayName(task)}`}
                    onClick={() => handleRemoveTask(task.id)}
                    disabled={isExecuting}
                  >
                    Usuń
                  </button>
                </div>

                {errors.length > 0 && (
                  <div className="alert alert-danger py-1 px-2 mb-2 small">
                    <ul className="mb-0 pl-3">
                      {errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {task.datetimeType && (
                  <div className="border-top pt-2 mt-2">
                    <div className="row g-2">
                      <div className="col-sm">
                        <label className="form-label-sm mb-1 font-weight-bold">
                          {task.datetimeType === "period" ? "Data od" : "Data i czas wykonania"}
                        </label>
                        <input
                          type="datetime-local"
                          className="form-control form-control-sm"
                          value={task.startAt || ""}
                          onChange={(e) =>
                            handleDateTimeChange(task.id, "startAt", e.target.value)
                          }
                          disabled={isExecuting}
                        />
                      </div>
                      
                      {task.datetimeType === "period" && (
                        <div className="col-sm">
                          <label className="form-label-sm mb-1 font-weight-bold">Data do</label>
                          <input
                            type="datetime-local"
                            className="form-control form-control-sm"
                            value={task.endAt || ""}
                            onChange={(e) =>
                              handleDateTimeChange(task.id, "endAt", e.target.value)
                            }
                            disabled={isExecuting}
                          />
                        </div>
                      )}
                    </div>
                    
                    {!task.disableReccuring && (
                      <div className="mt-2">
                        <label className="form-label-sm mb-1 font-weight-bold">
                          Częstotliwość powtarzania zlecenia
                        </label>
                        
                        {(() => {
                          const isCustomMode = customTaskIds.has(task.id);
                          const isPresetValue = [900, 1800, 3600, 7200, 21600, 43200, 86400].includes(Number(task.everyNth));
                          const selectValue = isCustomMode ? "custom" : (task.everyNth !== "" && task.everyNth !== null && task.everyNth !== undefined && isPresetValue) ? String(task.everyNth) : "";

                          return (
                            <div className="row g-2 align-items-center">
                              <div className="col-sm">
                                <select
                                  className="form-control form-control-sm"
                                  value={selectValue}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "custom") {
                                      setCustomTaskIds(prev => new Set(prev).add(task.id));
                                      if (!task.everyNth || Number(task.everyNth) < 900) {
                                        handleEveryNthChange(task.id, 900);
                                      }
                                    } else {
                                      setCustomTaskIds(prev => {
                                        const next = new Set(prev);
                                        next.delete(task.id);
                                        return next;
                                      });
                                      handleEveryNthChange(task.id, val);
                                    }
                                  }}
                                  disabled={isExecuting}
                                >
                                  <option value="">Brak powtarzania (jednorazowe)</option>
                                  <option value="900">Co 15 minut (900 sek.)</option>
                                  <option value="1800">Co 30 minut (1800 sek.)</option>
                                  <option value="3600">Co 1 godzinę (3600 sek.)</option>
                                  <option value="7200">Co 2 godziny (7200 sek.)</option>
                                  <option value="21600">Co 6 godzin (21 600 sek.)</option>
                                  <option value="43200">Co 12 godzin (43 200 sek.)</option>
                                  <option value="86400">Co 24 godziny / 1 dzień (86 400 sek.)</option>
                                  <option value="custom">Własny odstęp w sekundach...</option>
                                </select>
                              </div>

                              {(isCustomMode || (task.everyNth !== "" && task.everyNth !== null && task.everyNth !== undefined && !isPresetValue)) && (
                                <div className="col-sm-5 mt-2 mt-sm-0">
                                  <div className="input-group input-group-sm">
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={task.everyNth || ''}
                                      min={900}
                                      step={1}
                                      onChange={(e) => handleEveryNthChange(task.id, e.target.value)}
                                      disabled={isExecuting}
                                      placeholder="Sekundy (min. 900s)"
                                    />
                                    <span className="input-group-text bg-light text-muted small">sek.</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {(() => {
                          const sec = Number(task.everyNth);
                          if (!sec || isNaN(sec) || sec < 900) return null;

                          const durationLabel = formatDuration(sec);
                          const now = moment();
                          const startMoment = task.startAt ? moment(task.startAt) : now;
                          const firstRunText = startMoment.format('YYYY-MM-DD HH:mm:ss');
                          
                          const secondRunMoment = startMoment.clone().add(sec, 'seconds');
                          const secondRunText = secondRunMoment.format('YYYY-MM-DD HH:mm:ss');

                          let lastRunText = null;
                          let totalCycles = null;

                          if (task.endAt) {
                            const endMoment = moment(task.endAt);
                            if (endMoment.isAfter(startMoment)) {
                              const diffSec = endMoment.diff(startMoment, 'seconds');
                              const cycles = Math.floor(diffSec / sec);
                              const lastRunMoment = startMoment.clone().add(cycles * sec, 'seconds');
                              lastRunText = lastRunMoment.format('YYYY-MM-DD HH:mm:ss');
                              totalCycles = cycles + 1;
                            }
                          }

                          const isPastEndForSecond = task.endAt && moment(task.endAt).isBefore(secondRunMoment);

                          return (
                            <div className="alert alert-info border-info p-2 mt-2 mb-0 small">
                              <div className="font-weight-bold mb-1">
                                Częstotliwość cyklu: co {durationLabel} ({sec} sek. do VMC)
                              </div>
                              <div>
                                Pierwsze wykonanie: <strong>{firstRunText}</strong> {!task.startAt ? "(po wysłaniu, ± 15 min. komunikacji)" : "(± 15 min. komunikacji)"}
                              </div>
                              {!isPastEndForSecond && (
                                <div className="mt-1">
                                  Drugie wykonanie: <strong>{secondRunText}</strong> (+{durationLabel})
                                </div>
                              )}
                              {lastRunText && (
                                <div className="mt-1">
                                  Ostatnie wykonanie: <strong>{lastRunText}</strong> {totalCycles ? `(łącznie ${totalCycles} razy)` : ""}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        
        <div className="d-flex justify-content-center mt-3">
          <button
            className={`btn btn-primary btn-lg px-5 ${isExecuting || hasAnyErrors ? 'disabled' : ''}`}
            type="button"
            onClick={onExecute}
            disabled={isExecuting || tasks.length === 0 || hasAnyErrors}
          >
            {isExecuting ? (
              <>
                <span 
                  className="spinner-border spinner-border-sm mr-2" 
                  role="status" 
                  aria-hidden="true"
                ></span>
                Wysyłanie poleceń...
              </>
            ) : (
              `Wykonaj polecenia (${tasks.length})`
            )}
          </button>
        </div>
      </div>
    </section>
  );
};

export default TaskList;
